import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import ReactSVG from 'react-svg';
import { toast } from 'react-toastify';
import { disableBodyScroll } from 'body-scroll-lock';
import upload from 'upload_alt.svg';
import heart from 'heart_2d.svg';
import FavoriteSuccessBanner from '../CustomToast/FavoriteSuccessBanner';
import ErrorBoundary from '../ErrorBoundary';

function NavigationTabs({
  property,
  review_count,
  property_favorited,
  current_user,
}) {
  const isSSR = typeof window === 'undefined';

  const overviewRef = useRef(null);
  // const ratesRef = useRef(null);
  const availabilityRef = useRef(null);
  const amenitiesRef = useRef(null);
  const thingsRef = useRef(null);
  const reviewsRef = useRef(null);

  const [currentTab, setCurrentTab] = useState(null);
  const [checkOnScroll, setCheckOnScroll] = useState(true);
  const [favorited, setFavorited] = useState(false);

  // eslint-disable-next-line
  const setCurrentTabOnScroll = () => {
    if (!isSSR) {
      const overviewElem = document.querySelector('#overview');
      const availabilityElem = document.querySelector('#availability');
      const amenitiesElem = document.querySelector('#amenities');
      const thingsElem = document.querySelector('#things');
      const reviewsElem = document.querySelector('#reviews');
      if (checkOnScroll && document.querySelector('#overview')) {
        // Check in reverse order
        if (reviewsElem && reviewsElem.getBoundingClientRect().top <= 160) {
          setCurrentTab(reviewsRef);
        } else if (
          thingsElem &&
          thingsElem.getBoundingClientRect().top <= 160
        ) {
          setCurrentTab(thingsRef);
        } else if (
          amenitiesElem &&
          amenitiesElem.getBoundingClientRect().top <= 160
        ) {
          setCurrentTab(amenitiesRef);
        } else if (
          availabilityElem &&
          availabilityElem.getBoundingClientRect().top <= 160
        ) {
          setCurrentTab(availabilityRef);
          // } else if (ratesElem && ratesElem.getBoundingClientRect().top <= 160) {
          // setCurrentTab(ratesRef);
        } else if (
          overviewElem &&
          overviewElem.getBoundingClientRect().top <= 160
        ) {
          setCurrentTab(overviewRef);
        }
      }
    }
  };

  // Make navbar sticky and handle current tab on scroll
  useEffect(() => {
    const headerHeight = document.querySelector('.header-nav').offsetHeight;
    const navWrapper = document.querySelector('.navigation-wrapper');
    const pxFromTop = 520; // For desktop, the px from top will always be 520. Not showing navbar for mobile
    // const pxFromTop = navWrapper.offsetTop;

    const handleScroll = () => {
      setCurrentTabOnScroll();
      const contentWrapper = document.querySelector('.main-content-wrapper');
      if (window.scrollY + headerHeight >= pxFromTop) {
        navWrapper.classList.add('navigation-sticky');
        // TODO: Add padding to lower container
        if (contentWrapper) contentWrapper.style.paddingTop = '78px';
      } else {
        navWrapper.classList.remove('navigation-sticky');
        // TODO: Remove padding to lower container
        if (contentWrapper) contentWrapper.style.paddingTop = '28px';
      }
    };

    handleScroll();

    document.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line
    return () => document.removeEventListener('scroll', handleScroll);
  }, [setCurrentTabOnScroll]);

  // Set tab underline properties
  useEffect(() => {
    // Only fires on Mount
    if (overviewRef.current && !currentTab) setCurrentTab(overviewRef);
    if (currentTab) {
      // Set width of tab underline
      document.querySelector(
        '.navigation-underline'
      ).style.width = `${currentTab.current.clientWidth}px`;
      // Set transform of tab underline
      document.querySelector(
        '.navigation-underline'
      ).style.transform = `translateX(${currentTab.current.offsetLeft -
        7.5}px)`;
    }
  }, [currentTab]);

  const handleNavigation = selector => {
    setCheckOnScroll(false);
    switch (selector) {
      case 'overview':
        setCurrentTab(overviewRef);
        // Scroll element to top then add space for header, nav, and extra padding (50px + 90px + 20px)
        window.scrollTo(
          0,
          document.querySelector('#overview').getBoundingClientRect().top +
            window.pageYOffset -
            160
        );
        break;
      case 'availability':
        setCurrentTab(availabilityRef);
        // Scroll element to top then add space for header amd nav (50px + 90px)
        window.scrollTo(
          0,
          document.querySelector('#availability').getBoundingClientRect().top +
            window.pageYOffset -
            160
        );
        break;
      case 'amenities':
        setCurrentTab(amenitiesRef);
        // Scroll element to top then add space for header amd nav (50px + 90px)
        window.scrollTo(
          0,
          document.querySelector('#amenities').getBoundingClientRect().top +
            window.pageYOffset -
            160
        );
        break;
      case 'things':
        setCurrentTab(thingsRef);
        // Scroll element to top then add space for header amd nav (50px + 90px)
        window.scrollTo(
          0,
          document.querySelector('#things').getBoundingClientRect().top +
            window.pageYOffset -
            160
        );
        break;
      case 'reviews':
        setCurrentTab(reviewsRef);
        // Scroll element to top then add space for header amd nav (50px + 90px)
        window.scrollTo(
          0,
          document.querySelector('#reviews').getBoundingClientRect().top +
            window.pageYOffset -
            160
        );
        break;
      default:
        console.error(`No navigation case for ${selector}`);
        break;
    }
  };

  // Enable check on scroll if finished scrolling
  useEffect(() => {
    const checkScrollFinished = () => {
      if (!checkOnScroll) {
        if (
          // eslint-disable-next-line
          document
            .querySelector(`#${currentTab.current.dataset.id}`)
            .getBoundingClientRect().top -
            160 ===
          0
        ) {
          setCheckOnScroll(true);
        }
      }
    };

    document.addEventListener('scroll', checkScrollFinished);

    return () => document.removeEventListener('scroll', checkScrollFinished);
  }, [checkOnScroll, currentTab]);

  const [favoriteRoute, setFavoriteRoute] = useState(null);
  useEffect(() => {
    setFavoriteRoute(window.Routes.toggle_favorite_property_path(property));
  }, [property]);

  useEffect(() => {
    if (property_favorited) setFavorited(true);
  }, [property_favorited]);

  const handleFavorited = () => {
    if (current_user) {
      axios
        .patch(favoriteRoute, null, {
          headers: {
            'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
            Accept: 'application/json',
          },
        })
        .then(res => {
          if (res.status === 200) {
            toast.dismiss();
            if (!favorited)
              toast(<FavoriteSuccessBanner favorited type={property} />);
            else toast(<FavoriteSuccessBanner type={property} />);
            setFavorited(!favorited);
          }
        })
        .catch(err => {
          console.error(`%c ERROR: ${err}`, 'font-size: 28px');
          setFavorited(favorited);
          const event = new CustomEvent('toast-message', { detail: `${err}` });
          document.dispatchEvent(event);
          if (err.response.status === 401) {
            window.location.pathname = window.Routes.login_path();
          }
        });
    } else {
      const modal = document.querySelector('#popup-modal');
      modal.classList.remove('fadeOut');
      modal.classList.remove('hidden');
      modal.classList.add('fadeIn');
      disableBodyScroll(modal);
    }
  };

  return (
    <aside className="navigation-wrapper full-page-width no-padding">
      <div className="navigation-container">
        <nav className="navigation-tabs">
          <ul>
            {/* eslint-disable-next-line */}
            <li
              data-id="overview"
              ref={overviewRef}
              onClick={() => handleNavigation('overview')}
            >
              <a aria-label="Overview Section">Overview</a>
            </li>
            {/* eslint-disable-next-line */}
            <li
              data-id="availability"
              ref={availabilityRef}
              onClick={() => handleNavigation('availability')}
            >
              <a aria-label="Availability Section">Availability</a>
            </li>
            {/* eslint-disable-next-line */}
            <li
              data-id="amenities"
              ref={amenitiesRef}
              onClick={() => handleNavigation('amenities')}
            >
              <a aria-label="Amenities Section">Amenities</a>
            </li>
            {/* eslint-disable-next-line */}
            <li
              data-id="things"
              ref={thingsRef}
              onClick={() => handleNavigation('things')}
            >
              <a aria-label="Things To Do Section">Things To Do</a>
            </li>
            {/* eslint-disable-next-line */}
            <li
              data-id="reviews"
              ref={reviewsRef}
              onClick={() => handleNavigation('reviews')}
            >
              <a aria-label="Reviews Section">Reviews ({review_count})</a>
            </li>
          </ul>
          {currentTab && <div className="navigation-underline" />}
        </nav>

        <section className="property-nav-actions">
          <button
            aria-label="Share Property"
            name="Share Property"
            type="button"
            className="btn btn--small btn--light property-nav-action"
            onClick={() => {
              document
                .querySelector('#share_modal_container')
                .classList.remove('hidden');
              disableBodyScroll(document.querySelector('#popup-modal'));
            }}
          >
            <ReactSVG src={upload} />
            <span>Share</span>
          </button>
          <button
            aria-label="Toggle Favorited"
            name="Toggle Favorited"
            type="button"
            id="favorite_property"
            onClick={handleFavorited}
            className={`btn btn--small btn--light property-nav-action ${
              favorited ? 'favorited' : ''
            } heart-span-${property.id}`}
          >
            <ReactSVG src={heart} />
            <span>Save{favorited && 'd'}</span>
          </button>
        </section>
      </div>
    </aside>
  );
}

NavigationTabs.propTypes = {
  property: PropTypes.object.isRequired,
  review_count: PropTypes.number.isRequired,
};

export default function NavigationTabsWrapper({ ...props }) {
  return (
    <ErrorBoundary>
      <NavigationTabs {...props} />
    </ErrorBoundary>
  );
}
